import type { ProbabilityTag } from '@/domain/types'

/**
 * 概率 → 百分比文本。
 * 0–1% 之间保留一位小数（如 "0.5"），否则 toFixed(0) 会把 0.5% 四舍五入回
 * "0"/"1"，让概率展示下限（见 domain/probability.ts 的 PROB_FLOOR_*）失去意义。
 */
export function formatProbPercent(v: number): string {
  const pct = v * 100
  if (pct > 0 && pct < 1) return pct.toFixed(1)
  return pct.toFixed(0)
}

/**
 * 概率强度分级。
 *
 * 产品要求：除「数据上确实确定」（如段内第 1–2 位次、前 300 直录）外，
 * 不出现裸 100% / 0% 这类绝对表述，改用「极高概率 / 高概率 / 中等概率 /
 * 有可能 / 可能性不大 / 可能性很小」等措辞，让家长理解概率含不确定性。
 *
 * 判定只依赖点估计、区间下界与「结构性确定」标志，不污染 domain 概率真值：
 *  - certain：structuralCertain 且 lower ≥ 0.99 —— 仅当「前方人数 < 名额」
 *    （哪怕前方全报第一志愿也录得上）或前 300 直录时为真，才显示 100%。
 *    注意：低分段对保底校常因「估计前方很少人报该校」而点估计≈100%，但这是
 *    建立在先验偏好上的高概率而非结构性必录，此时 structuralCertain=false，
 *    一律封顶 99% 显示「极高概率」。
 *  - none：point ≤ 0 —— 资格线下等规则性 0，结构性无望，显示「—」。
 *    （floored 抬底后的极小概率 point>0，仍走正常分支，配合卡片既有
 *     「不代表绝对不可能」文案。）
 *  - 其余：point 封顶 99%、抬底沿用 PROB_FLOOR，绝不出现裸 100/0。
 */
export type ProbTone = 'certain' | 'high' | 'mid' | 'low' | 'none'

export interface ProbStrength {
  /** 强度词，如「极高概率」「基本确定录取」「基本无法录取」 */
  label: string
  /** 展示用百分比文本（不含 % 符号）；none 时为「—」 */
  display: string
  tone: ProbTone
  /** 是否结构性确定（可安全显示 100%、区间上界可保留真值） */
  certain: boolean
}

/**
 * 是否展示精确百分比（否则只给可能性描述词）。对齐「尾号分档」表：
 * - 前 300 / 结构性确定：给数字；
 * - 一中、二中：仅稳档（段内前 3 位，尾号 2/1/0）且达「稳」时给数字；
 * - 其他三所：较稳档（段内前 6 位，尾号 7~2）且达「稳」时给数字；
 * - 五五开 / 需保底（段内第 7–10 位）：只给文字，不给百分比。
 */
export const STABLE_POSITION_MAX = 3
export const HIGH_POSITION_MAX = 6
const MAIN_TWO = ['邹城一中', '邹城二中']

export function showProbPercent(
  school: string,
  tag: ProbabilityTag,
  structuralCertain: boolean,
  position: { inTop300: boolean; positionInBand: number }
): boolean {
  if (position.inTop300 || structuralCertain) return true
  if (tag !== 'safe') return false
  const maxPosition = MAIN_TWO.includes(school) ? STABLE_POSITION_MAX : HIGH_POSITION_MAX
  return position.positionInBand <= maxPosition
}

export function probStrength(
  point: number,
  lower: number,
  upper: number,
  structuralCertain = false
): ProbStrength {
  if (structuralCertain && lower >= 0.99) return { label: '基本确定录取', display: '100', tone: 'certain', certain: true }
  if (point <= 0) return { label: '基本无法录取', display: '—', tone: 'none', certain: false }
  const capped = Math.min(0.99, point)
  const pct = capped * 100
  const display = pct < 1 ? pct.toFixed(1) : pct >= 99 ? '99' : pct.toFixed(0)
  let label: string
  let tone: ProbTone
  if (point >= 0.85) {
    label = '极高概率'
    tone = 'high'
  } else if (point >= 0.65) {
    label = '高概率'
    tone = 'high'
  } else if (point >= 0.45) {
    label = '中等概率'
    tone = 'mid'
  } else if (point >= 0.25) {
    label = '有可能'
    tone = 'mid'
  } else if (point >= 0.08) {
    label = '可能性不大'
    tone = 'low'
  } else {
    label = '可能性很小'
    tone = 'low'
  }
  // upper 备用：保留参数签名一致性，便于未来按区间宽度细化措辞
  void upper
  return { label, display, tone, certain: false }
}

/** 强度词配色（Tailwind 文本色类名） */
export function probToneClass(tone: ProbTone): string {
  switch (tone) {
    case 'certain':
      return 'text-emerald-700'
    case 'high':
      return 'text-emerald-600'
    case 'mid':
      return 'text-amber-600'
    case 'low':
      return 'text-rose-600'
    case 'none':
    default:
      return 'text-ink-400'
  }
}
